import { memo, useMemo } from "react";
import { markerUrl } from "@/data/markers.ts";
import { poiIcon, poisForMap, type Poi } from "@/data/pois.ts";
import { gameToScreen, type Camera, type ProjectionConfig } from "@/engine/projection.ts";

interface PoiLayerProps {
  readonly mapId: string;
  readonly camera: Camera;
  readonly config: ProjectionConfig;
  readonly showLabels: boolean;
  readonly onSelect?: ((poi: Poi) => void) | undefined;
}

/**
 * Fixed points on the map, drawn with the game's own marker icons.
 *
 * Using the in-game art rather than invented shapes is the whole point: a
 * player glancing across from the game should recognise a tower as a tower
 * without translating between two visual languages.
 *
 * Icons do not scale with zoom. At 16 km wide anything that scales is either
 * invisible or enormous, and a marker you cannot see is a marker that is not
 * doing its job.
 */

/** Rendered icon size in screen pixels. Source art is 32x32. */
const ICON_SIZE = 26;
const ICON_SIZE_MINOR = 20;

/**
 * Place names get a plain dot rather than an icon.
 *
 * The game has no icon for "a place with a name" — the marker set covers
 * objectives, vehicles and callouts. Borrowing an unrelated emblem for sixty
 * landmarks would be sixty pieces of false signal. A dot says "something is
 * here, the word next to it is what it is called", which is exactly the job.
 * It is also what WARDOGS Hub does, so the two maps read alike.
 */
const LANDMARK_SIZE = 7;

/**
 * Below this zoom, place names are drawn as dots without their labels.
 *
 * Bakurani has sixty names inside about two kilometres of valley floor. Shown
 * all at once on a sixteen-kilometre overview they are not a map, they are a
 * smear — and a label you cannot read is worse than no label, because it
 * covers the terrain underneath as well.
 *
 * So zoomed out you get where things are, plus the headquarters that anchor
 * everything. Zoom in, or use the search, and the names arrive.
 */
const LANDMARK_LABEL_MIN_SCALE = 0.4;

/**
 * Landmark dots and their labels use a muted tan rather than a saturated
 * accent colour.
 *
 * Bakurani has dozens of named landmarks in a small area, so at a working
 * zoom the screen is already dense with dots and text. Giving every one of
 * them a loud colour made the whole map read as "urgent" everywhere at
 * once — nothing stood out because everything did. Landmarks are context,
 * not objectives, so they sit quietly in the background; towers and
 * headquarters keep the brighter, game-native icons that mark them as the
 * things worth acting on.
 */
const LANDMARK_COLOR = "oklch(0.62 0.045 85)";
const LANDMARK_LABEL_COLOR = "oklch(0.74 0.035 85)";

/**
 * Draw order priority: lower draws first (bottom), higher draws last (top).
 *
 * A landmark dot sitting a few pixels from a tower is common on the denser
 * maps, and SVG paints in document order — whichever element comes last in
 * the list wins the pixels it shares with an earlier one. Towers mark the Hot
 * Zone and headquarters anchor every route, so neither should ever end up
 * partially painted over by an ordinary place-name dot; both always draw on
 * top of it.
 */
const KIND_DRAW_ORDER: Record<Poi["kind"], number> = {
  landmark: 0,
  weaponsVendor: 0,
  garageVendor: 0,
  spawnBoard: 0,
  tower: 1,
  hq: 2,
};

function PoiLayerImpl({
  mapId,
  camera,
  config,
  showLabels,
  onSelect,
}: PoiLayerProps): React.ReactElement {
  const pois = useMemo(() => poisForMap(mapId), [mapId]);

  const placed = useMemo(() => {
    // Headquarters anchor every route and towers decide where the Hot Zone
    // goes, so both are drawn at any zoom — a commander looking at the whole
    // map needs to see the objectives without hunting for them. Everything
    // else appears once the view is tight enough for it to mean anything.
    const alwaysVisible = (poi: Poi): boolean => poi.kind === "hq" || poi.kind === "tower";

    const visible = pois
      .filter((poi) => alwaysVisible(poi) || camera.scale > 0.08)
      .map((poi) => ({
        poi,
        at: gameToScreen(poi.at, camera, config),
        size:
          poi.kind === "hq"
            ? ICON_SIZE
            : poi.kind === "landmark"
              ? LANDMARK_SIZE
              : ICON_SIZE_MINOR,
      }));

    const labelsWanted = showLabels;
    const result = declutter(visible, labelsWanted, camera.scale);

    // Label placement above needs its own priority order (headquarters, then
    // towers, get first pick of clear space). Painting order is a separate
    // concern — towers and headquarters must draw last regardless of which
    // POI happened to claim label space first — so it is resolved here, once,
    // rather than smuggled into declutter's ordering.
    return [...result].sort(
      (a, b) => KIND_DRAW_ORDER[a.poi.kind] - KIND_DRAW_ORDER[b.poi.kind],
    );
  }, [pois, camera, config, showLabels]);

  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full">
      {placed.map(({ poi, at, size, label }) => {
        const half = size / 2;
        const isLandmark = poi.kind === "landmark";

        return (
          <g
            key={poi.id}
            className={onSelect ? "pointer-events-auto cursor-pointer" : ""}
            onClick={onSelect ? () => onSelect(poi) : undefined}
          >
            {/* Generous invisible hit area: precise clicking is not possible
                while flying, and a small target is hostile on a touchscreen. */}
            <circle cx={at.x} cy={at.y} r={isLandmark ? 14 : 22} fill="transparent" />

            {isLandmark ? (
              <circle
                cx={at.x}
                cy={at.y}
                r={half}
                fill={LANDMARK_COLOR}
                stroke="oklch(0.12 0.008 250 / 0.8)"
                strokeWidth={1}
              />
            ) : (
              <>
                {/* A dark disc behind the icon so it survives snow and sand. */}
                <circle
                  cx={at.x}
                  cy={at.y}
                  r={half + 3}
                  fill="oklch(0.12 0.008 250 / 0.72)"
                  stroke="oklch(0.95 0.005 250 / 0.28)"
                  strokeWidth={1}
                />
                <image
                  href={markerUrl(poiIcon(poi))}
                  x={at.x - half}
                  y={at.y - half}
                  width={size}
                  height={size}
                  preserveAspectRatio="xMidYMid meet"
                />
              </>
            )}

            {label && (
              <>
                {label.needsLeader && (
                  <line
                    x1={at.x}
                    y1={at.y}
                    x2={at.x + label.dx}
                    y2={at.y + label.dy - 4}
                    stroke="oklch(0.58 0.012 250)"
                    strokeWidth={1}
                  />
                )}

                <text
                  x={at.x + label.dx}
                  y={at.y + label.dy}
                  textAnchor={label.anchor}
                  fontSize={poi.kind === "hq" ? 12 : 10.5}
                  fontWeight={poi.kind === "hq" ? 700 : isLandmark ? 400 : 500}
                  fontFamily="var(--font-mono)"
                  stroke="oklch(0.12 0.008 250 / 0.85)"
                  strokeWidth={isLandmark ? 2.25 : 3.5}
                  strokeLinejoin="round"
                  paintOrder="stroke"
                  fill={isLandmark ? LANDMARK_LABEL_COLOR : "oklch(0.95 0.005 250)"}
                >
                  {poi.label}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

interface LabelBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface LabelPlacement {
  /** Offset from the marker centre. */
  readonly dx: number;
  readonly dy: number;
  readonly anchor: "middle" | "start" | "end";
  /** True when a leader line is needed because the label sits off to a side. */
  readonly needsLeader: boolean;
}

interface PlacedPoi {
  readonly poi: Poi;
  readonly at: { x: number; y: number };
  readonly size: number;
  /** Where the label goes, or null when there was nowhere clear to put it. */
  readonly label: LabelPlacement | null;
}

const LABEL_HEIGHT = 13;

/**
 * Width of one character as a fraction of font size.
 *
 * The labels use a monospace font, so this is not an estimate — every glyph is
 * the same width and the box can be computed exactly. The previous version
 * reserved a flat 42 px for every label regardless of length, which under-
 * reserved badly for anything long: "South Town Forest West" needs about
 * 140 px, so it was declared clear of neighbours it was sitting on top of.
 */
const MONO_CHAR_RATIO = 0.6;

function labelWidth(text: string, fontSize: number): number {
  return text.length * fontSize * MONO_CHAR_RATIO;
}

/**
 * Decide where each marker's label goes.
 *
 * Every point keeps its own icon, always — nothing is ever merged or hidden
 * behind another marker. Icon overlap used to be collapsed into a
 * representative with a count badge, but on a dense cluster (Bakurani's five
 * towers, a warehouse district's dozen buildings) that read as one point
 * quietly swallowing others, which is more confusing than crowded icons: you
 * cannot tell what is under a badge without zooming in and hoping, and a
 * marker that only *sometimes* shows its own icon is not one a pilot can
 * trust at a glance.
 *
 * What labels still get is placement: a name that would overlap another
 * marker's icon, or another name already claimed, is nudged up, down or to a
 * side with a leader line rather than drawn on top of something. That part
 * of decluttering earns its keep — it is what makes names legible instead of
 * a smear — so it stays.
 *
 * Headquarters, then towers, get first pick of label position, since they
 * are the points everyone navigates from and should not be the ones nudged
 * out of the way by a landmark's name.
 */
function declutter(
  entries: Array<{ poi: Poi; at: { x: number; y: number }; size: number }>,
  showLabels: boolean,
  scale: number,
): PlacedPoi[] {
  const rank = (kind: Poi["kind"]): number => (kind === "hq" ? 0 : kind === "tower" ? 1 : 2);

  const ordered = [...entries].sort((a, b) => {
    return rank(a.poi.kind) - rank(b.poi.kind) || a.at.y - b.at.y;
  });

  // Find somewhere clear for each label.
  const occupied: LabelBox[] = [];

  // Markers themselves are obstacles — a label must not cover another's icon.
  for (const entry of ordered) {
    occupied.push({
      x: entry.at.x - entry.size / 2,
      y: entry.at.y - entry.size / 2,
      width: entry.size,
      height: entry.size,
    });
  }

  const result: PlacedPoi[] = [];

  for (const entry of ordered) {
    const isLandmark = entry.poi.kind === "landmark";
    const suppressed = !showLabels || (isLandmark && scale < LANDMARK_LABEL_MIN_SCALE);

    if (suppressed) {
      result.push({ ...entry, label: null });
      continue;
    }

    const fontSize = entry.poi.kind === "hq" ? 12 : 10.5;
    const width = labelWidth(entry.poi.label, fontSize);
    const half = entry.size / 2;
    const gap = 4;

    /**
     * Candidate positions, best first. Above is preferred because it reads
     * like a map should; the sides are next because horizontal space is what a
     * dense cluster actually has spare; below is last because it collides with
     * the next marker down in a north-to-south sweep.
     */
    const candidates: LabelPlacement[] = [
      { dx: 0, dy: -half - gap - 4, anchor: "middle", needsLeader: false },
      { dx: half + gap, dy: 4, anchor: "start", needsLeader: false },
      { dx: -half - gap, dy: 4, anchor: "end", needsLeader: false },
      { dx: 0, dy: half + gap + LABEL_HEIGHT, anchor: "middle", needsLeader: false },
      { dx: 0, dy: -half - gap - 4 - LABEL_HEIGHT, anchor: "middle", needsLeader: true },
      { dx: 0, dy: half + gap + LABEL_HEIGHT * 2, anchor: "middle", needsLeader: true },
      { dx: half + gap, dy: 4 - LABEL_HEIGHT, anchor: "start", needsLeader: true },
      { dx: -half - gap, dy: 4 + LABEL_HEIGHT, anchor: "end", needsLeader: true },
    ];

    let chosen: LabelPlacement | null = null;

    for (const candidate of candidates) {
      const left =
        candidate.anchor === "middle"
          ? entry.at.x + candidate.dx - width / 2
          : candidate.anchor === "start"
            ? entry.at.x + candidate.dx
            : entry.at.x + candidate.dx - width;

      const box: LabelBox = {
        x: left,
        y: entry.at.y + candidate.dy - LABEL_HEIGHT,
        width,
        height: LABEL_HEIGHT,
      };

      if (!occupied.some((other) => overlaps(box, other))) {
        occupied.push(box);
        chosen = candidate;
        break;
      }
    }

    // Nowhere clear: draw the marker without a label. An unreadable pile of
    // overlapping text hides the terrain as well as itself, so silence is the
    // better failure. The name is still reachable through search.
    result.push({ ...entry, label: chosen });
  }

  return result;
}

function overlaps(a: LabelBox, b: LabelBox): boolean {
  return (
    a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
  );
}

export const PoiLayer = memo(PoiLayerImpl);
